import { Button, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import * as Sentry from "@sentry/browser";
import clsx from "clsx";
import PropTypes from "prop-types";
import queryString from "query-string";
import React, { useEffect, useState } from "react";
import { useAlert } from "react-alert";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { Redirect } from "react-router-dom";
import { fetchAccountMetadata } from "./actions/environments";
import { fetchUsers } from "./actions/users";

import inviteImage from "./img/invite_confirm_switch_teams.png";
import inviteInvalidImage from "./img/invite_is_invalid.png";
import { signout } from "./NavBar";
import Topbar from "./Topbar";
import { makeRequest } from "./util";

// The logic here is a bit tricky. There are two different entry points
// into accepting an invite and there's some overlap between them.
//
// If the user clicks the accept invite link in the email, they get dropped
// into the AcceptInvitePage. If there's no problem accepting the invite, we
// accept it. If there was a problem, some scenarios we have to handle:
// - The invite was expired or sent to someone else -> we show an error message
// - The user is already part of an existing account -> we show a confirmation
//   page to confirm the user wants to switch accounts
//
// If the user logs in and they have pending invite to another account, they
// will land on PendingInvitePage. This will ask them to confirm if they want
// to switch accounts, and if so, we re-use the same logic as the
// AcceptInvitePage _after_ the user has confirmed they want to switch accounts.
//

// eslint-disable-next-line no-unused-vars
const [isValid, wrongEmail, inviteExpired, inviteAlreadyAccepted] = [
  0,
  1,
  2,
  3,
];

function AcceptInvitePage({ transferConfirmed = false, pendingInviteID }) {
  const location = useLocation();
  const alert = useAlert();
  const dispatch = useDispatch();

  const [transferTeamsConfirmed, setTransferTeamsConfirmed] = useState(
    transferConfirmed
  );
  const [needConfirmation, setNeedConfirmation] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [invalidInviteReason, setInvalidInviteReason] = useState(null);
  const [invalidInviteDetails, setInvalidInviteDetails] = useState("");

  const inviteID = pendingInviteID || queryString.parse(location.search).id;

  useEffect(() => {
    makeRequest("accept_invite", {
      invite_id: inviteID,
      confirmed: transferTeamsConfirmed,
    })
      .then((response) => {
        if (response.data.result === "success") {
          setInviteSuccess(true);
          dispatch(fetchAccountMetadata(alert));
          dispatch(fetchUsers());
        } else if (response.data.result === "invalid") {
          setInvalidInviteReason(response.data.invalid_reason);
          setInvalidInviteDetails(response.data.details);
        } else if (response.data.result === "needs_confirmation") {
          setNeedConfirmation(true);
        }
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to load page.");
        console.log(error);
        Sentry.captureException(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferTeamsConfirmed, inviteID]);

  if (inviteSuccess) {
    return <Redirect to="/events" />;
  } else if (needConfirmation) {
    return (
      <ConfirmSwitchTeams
        inviteID={inviteID}
        onConfirm={() => setTransferTeamsConfirmed(true)}
      />
    );
  } else if (invalidInviteReason) {
    return (
      <InvalidInvitePage
        reason={invalidInviteReason}
        details={invalidInviteDetails}
      />
    );
  } else {
    return null;
  }
}

export function PendingInvitePage() {
  const alert = useAlert();
  const history = useHistory();

  const [inviteID, setInviteID] = useState(null);
  const [fromEmail, setFromEmail] = useState(null);
  const [transferConfirmed, setTransferConfirmed] = useState(false);

  useEffect(() => {
    makeRequest("get_pending_invite", {})
      .then((response) => {
        if (!response.data.id) {
          history.push("/events");
        }

        setInviteID(response.data.id);
        setFromEmail(response.data.from_email);
      })
      .catch((error) => {
        console.log(error);
        Sentry.captureException(error);
      });
  }, []);

  if (inviteID) {
    if (!transferConfirmed) {
      return (
        <ConfirmSwitchTeams
          inviteID={inviteID}
          fromEmail={fromEmail}
          onConfirm={() => setTransferConfirmed(true)}
        />
      );
    } else {
      return (
        <AcceptInvitePage transferConfirmed={true} pendingInviteID={inviteID} />
      );
    }
  } else {
    return null;
  }
}

const useSwitchTeamsStyles = makeStyles(() => ({
  content: {
    paddingTop: 175,
    textAlign: "center",
  },
  header: {
    marginTop: 55,
  },
  paragraph: {
    textAlign: "center",
    display: "inline-block",
    width: 455,
    marginTop: 40,
  },
  subHeader: {
    textAlign: "center",
    display: "inline-block",
    width: 455,
    marginTop: 30,
  },
  buttons: {
    marginTop: 25,
  },
  spacing: {
    display: "inline-block",
    width: 60,
  },
}));

function ConfirmSwitchTeams({ onConfirm, inviteID, fromEmail }) {
  const classes = useSwitchTeamsStyles();
  const history = useHistory();

  const handleDecline = async () => {
    try {
      await makeRequest("decline_invite", { invite_id: inviteID });
      history.push("/events");
    } catch (e) {
      alert.error("Something went wrong. Unable to decline invite.");
      console.log(error);
      Sentry.captureException(error);
    }
  };

  return (
    <>
      <Topbar />
      <div className={clsx(classes.content, "flex flex-col items-center")}>
        <img alt="" src={inviteImage} />
        <Typography className={classes.header} component="h1" variant="h1">
          Join Organization
        </Typography>
        <Typography className={clsx(classes.paragraph)} component="p">
          You have received a pending invite{fromEmail && " from " + fromEmail}{" "}
          to a different account. Any data in your existing account will be
          lost. Would you like to accept the invite?
        </Typography>
        <div className={classes.buttons}>
          <Button size="large" variant="outlined" onClick={handleDecline}>
            Decline
          </Button>
          <div className={classes.spacing} />
          <Button
            size="large"
            variant="contained"
            color="primary"
            onClick={onConfirm}
          >
            Accept
          </Button>
        </div>
      </div>
    </>
  );
}

ConfirmSwitchTeams.propTypes = {
  onConfirm: PropTypes.func.isRequired,
};

function InvalidInvitePage({ reason, details }) {
  const classes = useSwitchTeamsStyles();

  var reasonString;

  if (reason === wrongEmail) {
    reasonString =
      "Reason: You are signed in with a different email than the one the invite was sent to.";
  } else if (reason === inviteExpired) {
    reasonString = "Reason: The invite has expired.";
  } else if (reason === inviteAlreadyAccepted) {
    reasonString = "Reason: The invite has already been accepted";
  } else {
    reasonString = "Please contact support@freshpaint.io";
  }

  return (
    <>
      <Topbar />
      <div className={clsx(classes.content, "flex flex-col items-center")}>
        <img alt="" src={inviteInvalidImage} />
        <Typography className={classes.header} component="h1" variant="h1">
          Error: Invalid Invite
        </Typography>
        <Typography className={clsx(classes.subHeader)} component="p">
          {reasonString}
        </Typography>
        {reason === wrongEmail && (
          <>
            <Typography className={clsx(classes.subHeader)} component="p">
              {details}
            </Typography>
          </>
        )}
        <div className={classes.buttons}>
          {reason === wrongEmail && (
            <>
              <Button size="large" variant="outlined" onClick={() => signout()}>
                Switch Email
              </Button>
              <div className={classes.spacing} />
            </>
          )}
          <Button
            size="large"
            variant="contained"
            color="primary"
            onClick={() => (window.location.href = "https://freshpaint.io")}
          >
            Back to Home
          </Button>
        </div>
      </div>
    </>
  );
}

InvalidInvitePage.propTypes = {
  reason: PropTypes.number,
  details: PropTypes.string,
};

export default AcceptInvitePage;
